<script setup lang="ts">
import { ref } from "vue";
import { PopoverClose } from "reka-ui";

import ConfirmPopover from "@/components/ConfirmPopover.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";

import { useMessageStore } from "@/stores/message-store";
import { useAnnotationStore } from "@/stores/annotation-store";

import type { Snapshot } from "@/models/Annotation";
import { saveAnnotation } from "@/services/AnnotationService";

withDefaults(defineProps<{ snapshot: Snapshot }>(), {  })

const selectedThemes = ref(
  __props.snapshot.bookmarks.length < 1
    ? __props.snapshot.associatedThemes.flatMap(({ rejected, theme }) => (rejected ? [] : theme))
    : __props.snapshot.bookmarks.map((b) => b.theme),
);

async function rejectThemes(arg: Snapshot, all?: boolean) {
  if (arg.status === "user") return;

  const a = useAnnotationStore().annotations.find((i) => i.markId === arg.uid);
  if (a == null) return;

  await useAnnotationStore().reject({
    annotation: a,
    reject: all ? { all } : { themes: selectedThemes.value },
  });
}

async function toggleInaccurate(entry: Snapshot) {
  if (entry.status === "rejected") {
    const a = useAnnotationStore().annotations.find((i) => i.markId === entry.uid);
    if (a == null) return;

    saveAnnotation({
      ...a,
      evidenceThemes: a.evidenceThemes?.map((i) => (/reject/i.test(i.status) ? { ...i, status: "candidate" } : i)),
    });

    useMessageStore().updateMessage({
      title: "Case summary action!",
      message: "Removed inaccurate mark from selected entry",
      type: "info",
      showReload: false,
      autoHide: true,
    });
  } else {
    await rejectThemes(entry);

    useMessageStore().updateMessage({
      title: "Case summary action!",
      message: "Selected themes have been rejected",
      type: "info",
      showReload: false,
      autoHide: true,
    });
  }
}
</script>
<template>
  <ConfirmPopover v-if="snapshot.bookmarks.length > 0" advanced>
    <template #trigger>
      <StringTooltip
        side="bottom"
        align="end"
        :item="snapshot.status === 'rejected' ? 'Remove inaccurate flag' : 'Mark as inaccurate'"
      >
        <button :id="`mark-inaccurate-${snapshot.uid}`" type="button" class="tw-mx-1">
          <i aria-hidden="true" class="fa-times-circle" :class="snapshot.status !== 'rejected' ? 'far' : 'fa'" />
        </button>
      </StringTooltip>
    </template>
    <template #question>
      <div class="tw-max-w-sm tw-space-y-3 focus:tw-border-black">
        <p class="tw-text-pretty">
          Are you sure you'd like to reject this snapshot and delete the bookmarks associated with the themes selected
          below?
        </p>
        <ul>
          <li
            v-for="{ theme, rejected } in snapshot.associatedThemes"
            :key="theme"
            class="tw-flex tw-items-center tw-justify-start tw-gap-2"
          >
            <template v-for="id in [`${theme}ToReject`]" :key="id">
              <input :id="id" v-model="selectedThemes" :disabled="rejected" :value="theme" type="checkbox" />
              <label :for="id" :class="{ 'tw-text-black/50 tw-line-through': rejected }">
                {{ theme }}
                <template v-if="snapshot.bookmarks.find((b) => b.theme === theme) == null">(AI suggested)</template>
              </label>
            </template>
          </li>
        </ul>
        <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
          <PopoverClose class="tw-btn-md-white tw-b-r">No</PopoverClose>
          <button type="button" class="tw-btn-md-gray tw-b-r tw-min-w-max" @click="rejectThemes(snapshot)">
            Reject Selected Themes
          </button>
          <button class="tw-btn-md-gray tw-b-r tw-min-w-max" @click="rejectThemes(snapshot, true)">
            Reject All themes
          </button>
        </div>
      </div>
    </template>
  </ConfirmPopover>
  <StringTooltip
    v-else
    align="end"
    side="bottom"
    :item="snapshot.status === 'rejected' ? 'Remove inaccurate flag' : 'Mark as inaccurate'"
  >
    <button
      :id="`mark-inaccurate-${snapshot.uid}`"
      type="button"
      class="tw-mx-1"
      @click.stop="toggleInaccurate(snapshot)"
    >
      <i :class="snapshot.status !== 'rejected' ? 'far' : 'fa'" class="fa-times-circle" />
    </button>
  </StringTooltip>
</template>
